import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';
import { ROUTE_DEFINITIONS } from '@/routes';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { SegmentedSwitch } from '@/components/ui/inputs/SegmentedSwitch';
import { Text } from '@/components/ui/text/Text';
import { useEmployeeList } from '@/hooks/employee/useEmployeeList';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { useEmployeeExport } from '@/hooks/employee/useEmployeeExport';
import { PermissionGuard } from '@/components/layout';
import { colors } from '@/assets/styles/colors';


export const EmployeeList: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    onSearch,
    onSortChange,
    loading,
  } = useEmployeeList();
  const { exportEmployees, isExporting } = useEmployeeExport({
    searchText: undefined,
    statusFilter: filters.statusFilter,
    contractStatusFilter: filters.contractStatusFilter,
  });

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="employee-list-page">
        <div className="flex flex-wrap gap-4 justify-between items-start">
          <PermissionGuard action="create">
            <Link to={ROUTE_DEFINITIONS.EMPLEADOS_CREAR.path}>
              <PrimaryButton iconLeft={<IconScout name="plus" />}>
                Nuevo colaborador
              </PrimaryButton>
            </Link>
          </PermissionGuard>
          <ExportDropdown
            onExport={exportEmployees}
            placement="right"
            isLoading={isExporting}
            availableFormats={['EXCEL']}
            disabled={total === 0}
          />
        </div>
        {/* Barra de filtros */}
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <FilterDropdown
            filters={filterConfig}
            values={{
              companyIds: filters.companyIds,
              contractStatusFilter: filters.contractStatusFilter,
            }}
            onChangeFilter={(key, value) => {
              setFilters(prev => ({
                ...prev,
                [key]: value,
              }));
            }}
          />
          <div className="flex flex-wrap gap-3 items-center">
            <Text variant="subheader-regular" color={colors.grays.neutral99} as="span">
              {translate('employee:listView.viewingLabel')}
            </Text>
            <SegmentedSwitch
              options={[
                { value: 'V', label: translate('employee:listView.statusSwitch.active') },
                { value: 'NV', label: translate('employee:listView.statusSwitch.inactive') },
              ]}
              value={filters.statusFilter}
              onChange={(value) => {
                setFilters(prev => ({
                  ...prev,
                  statusFilter: value as 'V' | 'NV',
                }));
              }}
              data-testid="employee-status-switch"
            />
          </div>
        </div>

        {/* Tabla */}
        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          searchable
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          columnMaxWidth={150}
          pagination={{
            page,
            pageSize,
            total: total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'fullName', direction: 'asc' }}
          data-testid="employee-table"
          isLoading={loading}
        />
      </div>
    </PageLayout>
  );
};

export default EmployeeList;
